/**
 * Stable numbers for the user-facing error messages, so a user can read one off a toast and support
 * can point at exactly one failure site instead of guessing from a paraphrased message.
 *
 * Conventions:
 *  - Only failures a user might contact support about get a number. Something they can see and fix
 *    themselves — a date in the past, an empty field — is a warning: `showWarningToast`, or a
 *    response marked `severity: 'warning'`, which shows the message alone.
 *  - A number is permanent. Reword the message whenever you like; never hand a retired number to a
 *    different failure.
 *  - `1xxx` is raised here in the browser, `2xxx` by the server (those live inline in the PHP
 *    controllers). The areas line up, so a server code is its browser counterpart plus 1000: 1110
 *    and 2110 both mean "adding to the calendar failed", the 2xxx one just got far enough to say why.
 *  - Hundreds digit is the area: 0 generic, 1 scheduling, 2 workout export, 3 activity download,
 *    4 merging, 5 workout players, 6 Strava, 7 activity tags.
 *
 * `docs/error-codes.md` is the catalog support reads.
 */
export const ErrorCode = {
  /** The request never reached Xert at all. */
  NETWORK_UNREACHABLE: 1000,

  // Scheduling: 11xx.
  /** Rejected before we call the server. */
  SCHEDULE_NO_TRAINING: 1101,
  // 1102 and 1103 are retired: "pick a valid date" and "pick a future date" are the user's own form
  // to fix, so they show as warnings with no number. Don't hand those numbers to anything else.
  SCHEDULE_CALENDAR_FAILED: 1110,
  SCHEDULE_FORECAST_FAILED: 1111,

  // Workout export: 12xx.
  /** The server would not produce the file. */
  WORKOUT_EXPORT_FAILED: 1200,
  /** We got a file but could not hand it to the browser. */
  WORKOUT_DOWNLOAD_FAILED: 1201,

  // Activity file download: 13xx.
  ACTIVITY_FILE_UNAVAILABLE: 1300,
  ACTIVITY_DOWNLOAD_FAILED: 1301,

  // Merging activities: 14xx.
  MERGE_FAILED: 1400,
  /** The merge request itself threw, rather than coming back with a reason. */
  MERGE_THREW: 1401,

  // Workout players (Zwift, Wahoo, Garmin Connect, ...): 15xx.
  WORKOUT_PLAYER_SEND_FAILED: 1500,
  WORKOUT_PLAYER_CLEAR_FAILED: 1501,
  /** Garmin Connect, Zwift and Wahoo Sync pages: sending workouts, activity sync or activity types. */
  SYNC_SETTING_FAILED: 1502,
  /** Zwift and Wahoo Sync pages: removing or unscheduling a workout sent to the app. */
  SYNC_WORKOUT_REMOVE_FAILED: 1503,
  /** Garmin Connect Sync page: asking Garmin for past activities. */
  GARMIN_IMPORT_REQUEST_FAILED: 1504,

  // Strava and social reports: 16xx.
  STRAVA_SEND_FAILED: 1600,
  STRAVA_REPORT_FAILED: 1601,
  SOCIAL_REPORT_DATA_FAILED: 1602,
  SOCIAL_REPORT_BACKGROUND_FAILED: 1603,
  SOCIAL_REPORT_SAVE_FAILED: 1604,
  SOCIAL_REPORT_IMAGE_FAILED: 1605,
  /** Strava Sync page: listing the Strava activities to import. */
  STRAVA_ACTIVITIES_FAILED: 1606,
  STRAVA_IMPORT_FAILED: 1607,
  /** Strava Sync page: automatic sync, activity types or Strava reports. */
  STRAVA_SETTING_FAILED: 1608,
  /** Showing your name and photo on other riders' group reports. */
  GROUP_REPORT_PRIVACY_FAILED: 1609,
  /** Admin, Report Backgrounds: loading the stock backgrounds. */
  STOCK_BACKGROUNDS_FAILED: 1610,
  /** Admin, Report Backgrounds: adding or removing a stock background. */
  STOCK_BACKGROUND_SAVE_FAILED: 1611,

  // Activity tags: 17xx.
  ACTIVITY_TAG_UPDATE_FAILED: 1700,
  ACTIVITY_TAG_CREATE_FAILED: 1701,
  ACTIVITY_TAG_DELETE_FAILED: 1702,
} as const;

export type ErrorCodeValue = (typeof ErrorCode)[keyof typeof ErrorCode];
